import axios, { AxiosRequestConfig } from "axios";
import { toast } from "react-toastify";

// Configure axios timeout for long-running operations (15 minutes)
axios.defaults.timeout = 900000; // 900 seconds = 15 minutes

/** A readable message for a failed request, taken from the server's response where possible. */
export function errorMessage(err: any): string {
  const response = err?.response;
  if (!response) {
    return "Could not reach the server. Check your connection and try again.";
  }

  const data = response.data;
  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }
  if (data && typeof data === "object") {
    // ValidationProblemDetails: { errors: { Field: ["message", ...] } }
    if (data.errors && typeof data.errors === "object") {
      const messages = Object.values(data.errors).flat().filter(Boolean);
      if (messages.length) {
        return messages.join(" ");
      }
    }
    // ProblemDetails (detail/title) and the { error } / { errorMessage } bodies some endpoints return
    const text = data.detail || data.errorMessage || data.error || data.message || data.title;
    if (typeof text === "string" && text.trim()) {
      return text.trim();
    }
  }
  return `The request failed (HTTP ${response.status}).`;
}

/**
 * Follows the server's redirect (e.g. to sign in) when it sends one; otherwise tells the user what failed.
 * Returns true when it redirected.
 */
function handleError(err: any): boolean {
  const location = err?.response?.headers?.location;
  if (location) {
    window.location = location;
    return true;
  }

  const message = errorMessage(err);
  console.log(err);
  // toastId collapses repeats of the same failure, e.g. from polling
  toast.error(message, { toastId: message });
  return false;
}

/** An error carrying the server's message, so callers can show or log it. */
function apiError(err: any): Error {
  const error: any = new Error(errorMessage(err));
  error.response = err?.response;
  return error;
}

interface GetOptions {
  /** Reject on failure (after the toast), for GETs that trigger an action and report success. */
  rejectOnError?: boolean;
}

function get<T>(url: string, config?: AxiosRequestConfig, options?: GetOptions): Promise<T> {
  return axios
    .get(url, config)
    .then((a) => a.data)
    .catch((err: any) => {
      if (handleError(err) || !options?.rejectOnError) {
        return;
      }
      throw apiError(err);
    });
}

function post<T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> {
  return axios
    .post(url, data, {
      ...config,
      headers: {
        "Content-Type": "application/json",
        ...config?.headers,
      },
    })
    .then((a) => a.data)
    .catch((err: any) => {
      if (handleError(err)) {
        return;
      }
      throw apiError(err);
    });
}

function put<T>(url: string, data?: any): Promise<T> {
  return axios
    .put(url, data, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((a) => a.data)
    .catch((err: any) => {
      if (handleError(err)) {
        return;
      }
      throw apiError(err);
    });
}

function remove<T>(url: string): Promise<T> {
  return axios
    .delete(url)
    .then((a) => a.data)
    .catch((err: any) => {
      if (handleError(err)) {
        return;
      }
      throw apiError(err);
    });
}

const restClient = {
  get,
  post,
  put,
  remove,
  axios,
};

export default restClient;
